import React, { useState, useRef, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { ChevronsUpDown, Check, Building2, Users } from "lucide-react";

const ROLE_COLOR = { owner: "#34D399", admin: "#60A5FA", member: "#FBBF24", viewer: "#A1A1AA" };

// Lets a user switch between their own workspace and any they've been invited to.
export default function WorkspaceSwitcher() {
  const { t, workspaces, activeWorkspace, switchWorkspace } = useApp();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!workspaces || workspaces.length <= 1) return null; // nothing to switch to yet
  const current = activeWorkspace || workspaces[0];

  return (
    <div className="relative px-3 pt-3" ref={ref} data-testid="workspace-switcher">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="workspace-switcher-button"
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border hover:bg-surface-hover transition-colors text-left"
      >
        {current?.is_personal ? <Building2 className="w-4 h-4 text-text-secondary shrink-0" />
          : <Users className="w-4 h-4 text-text-secondary shrink-0" />}
        <span className="flex-1 min-w-0">
          <span className="block text-xs font-medium truncate">{current?.name}</span>
          <span className="text-[10px] capitalize" style={{ color: ROLE_COLOR[current?.role] }}>
            {t(`role_${current?.role}`)}
          </span>
        </span>
        <ChevronsUpDown className="w-3.5 h-3.5 text-text-secondary shrink-0" />
      </button>

      {open && (
        <div
          className="absolute left-3 right-3 mt-1 z-50 rounded-xl border bg-surface p-1 max-h-80 overflow-y-auto"
          style={{ boxShadow: "var(--shadow)" }}
          data-testid="workspace-switcher-menu"
        >
          {workspaces.map((w) => (
            <button
              key={w.id}
              onClick={() => { switchWorkspace(w.id); setOpen(false); }}
              data-testid={`workspace-option-${w.id}`}
              className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-surface-hover transition-colors text-left"
            >
              {w.is_personal ? <Building2 className="w-3.5 h-3.5 text-text-secondary shrink-0" />
                : <Users className="w-3.5 h-3.5 text-text-secondary shrink-0" />}
              <span className="flex-1 min-w-0">
                <span className="block text-xs font-medium truncate">{w.name}</span>
                <span className="text-[10px] capitalize" style={{ color: ROLE_COLOR[w.role] }}>
                  {t(`role_${w.role}`)}
                </span>
              </span>
              {current?.id === w.id && <Check className="w-3.5 h-3.5 text-text-primary shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
